import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import {
  getCostingDashboardStats,
  getStandardCosts,
  getActualCosts,
  recalculateAllCosts,
  type ProductionCostRecord,
} from '@/services/costingService';
import { useWarehouseStore } from '@/stores/warehouseStore';
import { formatINR } from '@/utils/format';

interface DashboardStats {
  totalProdCost: number;
  avgMargin: number;
  hasMargin: boolean;
  donutData: { label: string; value: number; pct: number; color: string }[];
  ordersCosted: number;
  ordersWithActual: number;
  ordersStandardOnly: number;
  overBudget: number;
  underBudget: number;
  onBudget: number;
  totalStandard: number;
  totalActual: number;
  totalVariance: number;
  variancePct: number;
}

function formatQty(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
}

function shortDate(value?: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' });
}

const marginClass = (pct: number) => (pct >= 30 ? 'text-emerald-600' : pct >= 10 ? 'text-amber-600' : 'text-rose-600');

/** Actual − standard: spending more than standard is unfavourable */
const varianceClass = (v: number) => (v > 0 ? 'text-rose-600' : v < 0 ? 'text-emerald-600' : 'text-slate-500');

function Panel({
  title,
  count,
  action,
  className = '',
  children,
}: {
  title: string;
  count?: number;
  action?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={`bg-white border border-slate-200 rounded-xl flex flex-col min-h-0 ${className}`}>
      <div className="flex items-center justify-between px-3.5 py-2.5 border-b border-slate-100 shrink-0">
        <h2 className="text-xs font-bold text-[#1e293b] uppercase tracking-wide">
          {title}
          {count !== undefined && <span className="ml-1.5 text-[#94a3b8] font-semibold">({count})</span>}
        </h2>
        {action}
      </div>
      <div className="overflow-auto min-h-0 flex-1">{children}</div>
    </div>
  );
}

export default function CostingDashboardPage() {
  const navigate = useNavigate();
  const { selectedWarehouseId } = useWarehouseStore();
  const [toast, setToast] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [recalculating, setRecalculating] = useState(false);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [standardCosts, setStandardCosts] = useState<ProductionCostRecord[]>([]);
  const [actualCosts, setActualCosts] = useState<ProductionCostRecord[]>([]);
  const [search, setSearch] = useState('');

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3500);
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, stdRes, actRes] = await Promise.all([
        getCostingDashboardStats(selectedWarehouseId),
        getStandardCosts('', '', selectedWarehouseId),
        getActualCosts('', 'all', selectedWarehouseId),
      ]);
      setStats(statsRes?.success ? statsRes.data : null);
      setStandardCosts(stdRes?.success && Array.isArray(stdRes.data) ? stdRes.data : []);
      setActualCosts(actRes?.success && Array.isArray(actRes.data) ? actRes.data : []);
    } catch (err) {
      console.error('Error loading costing dashboard data:', err);
      setStats(null);
      setStandardCosts([]);
      setActualCosts([]);
    } finally {
      setLoading(false);
    }
  }, [selectedWarehouseId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Recalculates every saved cost record from the orders' current BOM, issues, shop floor time and rates
  const handleRecalculateAll = async () => {
    if (recalculating) return;
    setRecalculating(true);
    try {
      const res = await recalculateAllCosts();
      showToast(res?.message || (res?.success ? 'Cost records recalculated' : 'Failed to recalculate cost records'));
      if (res?.success) await loadData();
    } catch (err: any) {
      showToast(err?.message || 'Failed to recalculate cost records');
    } finally {
      setRecalculating(false);
    }
  };

  // One row per production order: its standard and actual cost side by side
  const orderRows = useMemo(() => {
    const byOrder = new Map<string, { std?: ProductionCostRecord; act?: ProductionCostRecord }>();
    for (const c of standardCosts) {
      if (c.costingMethod !== 'STANDARD') continue;
      const key = c.productionOrderId || c.productionOrderNumber;
      byOrder.set(key, { ...(byOrder.get(key) || {}), std: c });
    }
    for (const c of actualCosts) {
      if (c.costingMethod !== 'ACTUAL') continue;
      const key = c.productionOrderId || c.productionOrderNumber;
      byOrder.set(key, { ...(byOrder.get(key) || {}), act: c });
    }
    return [...byOrder.entries()]
      .map(([key, { std, act }]) => {
        const base = (act || std) as ProductionCostRecord;
        const variance = std && act ? act.totalCost - std.totalCost : null;
        return {
          key,
          poNumber: base.productionOrderNumber,
          productId: base.productId,
          productName: base.productName,
          qty: base.producedQty,
          unit: base.unit,
          stdTotal: std ? std.totalCost : null,
          actTotal: act ? act.totalCost : null,
          stdPerUnit: std ? std.costPerUnit : null,
          actPerUnit: act ? act.costPerUnit : null,
          variance,
          variancePct: variance !== null && std && std.totalCost > 0 ? (variance / std.totalCost) * 100 : null,
          salePrice: base.salePrice || 0,
          marginPct: base.salePrice > 0 ? base.marginPct : null,
          grade: base.grade,
          status: base.status,
          calculatedAt: base.calculatedAt || base.createdAt,
        };
      })
      .sort((a, b) => String(b.calculatedAt || '').localeCompare(String(a.calculatedAt || '')));
  }, [standardCosts, actualCosts]);

  const filteredOrders = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return orderRows;
    return orderRows.filter((r) => r.poNumber?.toLowerCase().includes(q) || r.productName?.toLowerCase().includes(q));
  }, [orderRows, search]);

  // Average cost per unit of each product across its costed orders
  const productRows = useMemo(() => {
    const byProduct = new Map<string, { name: string; orders: number; stdCost: number; stdQty: number; actCost: number; actQty: number; salePrice: number }>();
    for (const r of orderRows) {
      const key = r.productId || r.productName;
      const row = byProduct.get(key) || { name: r.productName, orders: 0, stdCost: 0, stdQty: 0, actCost: 0, actQty: 0, salePrice: 0 };
      row.orders += 1;
      if (r.stdPerUnit !== null && r.stdTotal !== null) {
        row.stdCost += r.stdTotal;
        row.stdQty += r.stdPerUnit > 0 ? r.stdTotal / r.stdPerUnit : 0;
      }
      if (r.actPerUnit !== null && r.actTotal !== null) {
        row.actCost += r.actTotal;
        row.actQty += r.actPerUnit > 0 ? r.actTotal / r.actPerUnit : 0;
      }
      if (r.salePrice > 0) row.salePrice = r.salePrice;
      byProduct.set(key, row);
    }
    return [...byProduct.values()]
      .map((p) => {
        const std = p.stdQty > 0 ? p.stdCost / p.stdQty : null;
        const act = p.actQty > 0 ? p.actCost / p.actQty : null;
        const cost = act ?? std;
        return {
          ...p,
          stdPerUnit: std,
          actPerUnit: act,
          variance: std !== null && act !== null ? act - std : null,
          marginPct: p.salePrice > 0 && cost !== null ? ((p.salePrice - cost) / p.salePrice) * 100 : null,
        };
      })
      .sort((a, b) => b.orders - a.orders);
  }, [orderRows]);

  const donutData = stats?.donutData || [];
  const donutGradient = useMemo(() => {
    let current = 0;
    const stops: string[] = [];
    for (const d of donutData) {
      if (d.pct <= 0) continue;
      stops.push(`${d.color} ${current.toFixed(2)}% ${(current + d.pct).toFixed(2)}%`);
      current += d.pct;
    }
    return stops.length > 0 ? `conic-gradient(${stops.join(', ')})` : 'conic-gradient(#E5E7EB 0% 100%)';
  }, [donutData]);

  const comparable = (stats?.overBudget || 0) + (stats?.underBudget || 0) + (stats?.onBudget || 0);
  const kpis = [
    {
      label: 'Total Production Cost',
      value: formatINR(stats?.totalProdCost || 0),
      sub: 'actual where available, else standard',
      tone: 'text-[#1e293b]',
    },
    {
      label: 'Orders Costed',
      value: String(stats?.ordersCosted || 0),
      sub: `${stats?.ordersWithActual || 0} with actual · ${stats?.ordersStandardOnly || 0} standard only`,
      tone: 'text-sky-600',
    },
    {
      label: 'Actual vs Standard',
      value: comparable > 0 ? `${(stats?.totalVariance || 0) > 0 ? '+' : ''}${formatINR(stats?.totalVariance || 0)}` : '—',
      sub: comparable > 0 ? `${(stats?.variancePct || 0).toFixed(1)}% on ${comparable} order(s)` : 'no order has both costs yet',
      tone: comparable > 0 ? varianceClass(stats?.totalVariance || 0) : 'text-[#94a3b8]',
    },
    {
      label: 'Over Budget',
      value: String(stats?.overBudget || 0),
      sub: `${stats?.onBudget || 0} on budget · ${stats?.underBudget || 0} under`,
      tone: (stats?.overBudget || 0) > 0 ? 'text-rose-600' : 'text-emerald-600',
    },
    {
      label: 'Average Margin',
      value: stats?.hasMargin ? `${stats.avgMargin.toFixed(1)}%` : '—',
      sub: stats?.hasMargin ? 'on items with a sale price' : 'set a sale rate on the items',
      tone: stats?.hasMargin ? marginClass(stats.avgMargin) : 'text-[#94a3b8]',
    },
  ];

  const navButtons = [
    { label: 'Order Costing', icon: 'ri-calculator-line', path: '/manufacturing/costing/orders' },
    { label: 'Standard', icon: 'ri-file-list-3-line', path: '/manufacturing/costing/standard' },
    { label: 'Actual', icon: 'ri-money-rupee-circle-line', path: '/manufacturing/costing/actual' },
    { label: 'Variance', icon: 'ri-line-chart-line', path: '/manufacturing/costing/variance' },
    { label: 'Setup', icon: 'ri-settings-3-line', path: '/manufacturing/costing/setup' },
  ];

  return (
    <AppLayout>
      <div className="min-h-screen bg-[#f8fafc] py-4 px-4 md:px-6">
        <div className="max-w-[1600px] mx-auto space-y-3">
          {toast && (
            <div className="fixed top-4 right-4 z-50 bg-gray-900 text-white text-sm px-4 py-2 rounded-lg shadow-lg max-w-md">
              {toast}
            </div>
          )}

          {/* Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold text-[#1e293b]">Manufacturing Costing</h1>
              {loading && (
                <span className="inline-flex items-center gap-1 text-xs text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full">
                  <i className="ri-loader-4-line animate-spin text-xs" /> Loading
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              {navButtons.map((b) => (
                <button
                  key={b.path}
                  onClick={() => navigate(b.path)}
                  className="inline-flex items-center gap-1.5 h-8 px-2.5 text-xs font-medium text-[#475569] bg-white border border-slate-200 rounded-lg hover:bg-slate-50 cursor-pointer whitespace-nowrap"
                >
                  <i className={b.icon} />
                  {b.label}
                </button>
              ))}
              <button
                onClick={handleRecalculateAll}
                disabled={recalculating}
                className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-semibold text-white bg-[#1e293b] rounded-lg hover:bg-slate-700 cursor-pointer whitespace-nowrap disabled:opacity-60"
                title="Recalculate every saved cost record from current BOM, material issues, shop floor time and rates"
              >
                <i className={recalculating ? 'ri-loader-4-line animate-spin' : 'ri-refresh-line'} />
                {recalculating ? 'Recalculating…' : 'Recalculate All'}
              </button>
            </div>
          </div>

          {/* KPI strip */}
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-2.5">
            {kpis.map((k) => (
              <div key={k.label} className="bg-white border border-slate-200 rounded-xl px-3 py-2.5">
                <div className="text-[11px] text-[#64748b] font-medium truncate">{k.label}</div>
                <div className={`text-lg font-bold leading-tight ${k.tone}`}>{k.value}</div>
                <div className="text-[10px] text-[#94a3b8] truncate" title={k.sub}>{k.sub}</div>
              </div>
            ))}
          </div>

          {/* Cost mix + product costs */}
          <div className="grid grid-cols-1 xl:grid-cols-5 gap-3">
            <Panel title="Cost Mix" className="xl:col-span-2 max-h-[250px]">
              <div className="flex items-center gap-5 px-4 py-3">
                <div className="relative w-[120px] h-[120px] rounded-full shrink-0" style={{ background: donutGradient }}>
                  <div className="absolute inset-[22px] bg-white rounded-full flex flex-col items-center justify-center">
                    <span className="text-[10px] text-[#94a3b8]">Total</span>
                    <span className="text-xs font-bold text-[#1e293b]">{formatINR(stats?.totalProdCost || 0)}</span>
                  </div>
                </div>
                <div className="flex-1 space-y-1.5 min-w-0">
                  {donutData.map((d) => (
                    <div key={d.label} className="flex items-center gap-2 text-xs">
                      <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: d.color }} />
                      <span className="text-[#475569] w-20 shrink-0">{d.label}</span>
                      <span className="font-semibold text-[#1e293b] flex-1 text-right truncate">{formatINR(d.value)}</span>
                      <span className="text-[#94a3b8] w-12 text-right shrink-0">{d.pct.toFixed(1)}%</span>
                    </div>
                  ))}
                  {donutData.length === 0 && <div className="text-xs text-[#94a3b8]">No cost records yet</div>}
                </div>
              </div>
            </Panel>

            <Panel title="Cost per Unit by Product" count={productRows.length} className="xl:col-span-3 max-h-[250px]">
              {productRows.length === 0 ? (
                <div className="px-4 py-6 text-center text-xs text-[#94a3b8]">No products costed yet</div>
              ) : (
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-slate-50 text-[#64748b] text-[11px]">
                    <tr>
                      <th className="text-left px-3.5 py-2 font-semibold">Product</th>
                      <th className="text-right px-3 py-2 font-semibold">Orders</th>
                      <th className="text-right px-3 py-2 font-semibold">Std / Unit</th>
                      <th className="text-right px-3 py-2 font-semibold">Act / Unit</th>
                      <th className="text-right px-3 py-2 font-semibold">Variance</th>
                      <th className="text-right px-3 py-2 font-semibold">Sale Price</th>
                      <th className="text-right px-3.5 py-2 font-semibold">Margin</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {productRows.map((p) => (
                      <tr key={p.name} className="hover:bg-slate-50/60">
                        <td className="px-3.5 py-2 font-medium text-[#1e293b]">{p.name}</td>
                        <td className="px-3 py-2 text-right text-[#64748b]">{p.orders}</td>
                        <td className="px-3 py-2 text-right text-[#475569]">{p.stdPerUnit === null ? '—' : formatINR(p.stdPerUnit)}</td>
                        <td className="px-3 py-2 text-right text-[#475569]">{p.actPerUnit === null ? '—' : formatINR(p.actPerUnit)}</td>
                        <td className={`px-3 py-2 text-right font-semibold ${p.variance === null ? 'text-[#94a3b8]' : varianceClass(p.variance)}`}>
                          {p.variance === null ? '—' : `${p.variance > 0 ? '+' : ''}${formatINR(p.variance)}`}
                        </td>
                        <td className="px-3 py-2 text-right text-[#475569]">{p.salePrice > 0 ? formatINR(p.salePrice) : '—'}</td>
                        <td className={`px-3.5 py-2 text-right font-semibold ${p.marginPct === null ? 'text-[#94a3b8]' : marginClass(p.marginPct)}`}>
                          {p.marginPct === null ? '—' : `${p.marginPct.toFixed(1)}%`}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Panel>
          </div>

          {/* Orders */}
          <Panel
            title="Costed Production Orders"
            count={filteredOrders.length}
            className="max-h-[calc(100vh-470px)] min-h-[240px]"
            action={
              <div className="relative">
                <i className="ri-search-line absolute left-2 top-1/2 -translate-y-1/2 text-[#94a3b8] text-xs" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search order or product"
                  className="h-7 w-52 pl-7 pr-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-200"
                />
              </div>
            }
          >
            {filteredOrders.length === 0 ? (
              <div className="px-4 py-8 text-center text-xs text-[#94a3b8]">
                {orderRows.length === 0
                  ? 'No cost records yet. Calculate a cost from Order Costing, or complete a production order.'
                  : 'No orders match your search'}
              </div>
            ) : (
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-slate-50 text-[#64748b] text-[11px]">
                  <tr>
                    <th className="text-left px-3.5 py-2 font-semibold">Order</th>
                    <th className="text-left px-3 py-2 font-semibold">Product</th>
                    <th className="text-right px-3 py-2 font-semibold">Qty</th>
                    <th className="text-right px-3 py-2 font-semibold">Standard</th>
                    <th className="text-right px-3 py-2 font-semibold">Actual</th>
                    <th className="text-right px-3 py-2 font-semibold">Variance</th>
                    <th className="text-right px-3 py-2 font-semibold">Cost / Unit</th>
                    <th className="text-right px-3 py-2 font-semibold">Margin</th>
                    <th className="text-center px-3 py-2 font-semibold">Grade</th>
                    <th className="text-right px-3.5 py-2 font-semibold">Calculated</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredOrders.map((r) => (
                    <tr
                      key={r.key}
                      onClick={() => navigate('/manufacturing/costing/variance')}
                      className="hover:bg-slate-50/60 cursor-pointer"
                      title="Open variance analysis"
                    >
                      <td className="px-3.5 py-2 font-semibold text-[#4f46e5] whitespace-nowrap">{r.poNumber}</td>
                      <td className="px-3 py-2 text-[#1e293b]">{r.productName}</td>
                      <td className="px-3 py-2 text-right text-[#64748b] whitespace-nowrap">
                        {formatQty(r.qty)} {r.unit}
                      </td>
                      <td className="px-3 py-2 text-right text-[#475569]">{r.stdTotal === null ? '—' : formatINR(r.stdTotal)}</td>
                      <td className="px-3 py-2 text-right text-[#475569]">{r.actTotal === null ? '—' : formatINR(r.actTotal)}</td>
                      <td className={`px-3 py-2 text-right font-semibold whitespace-nowrap ${r.variance === null ? 'text-[#94a3b8]' : varianceClass(r.variance)}`}>
                        {r.variance === null
                          ? '—'
                          : `${r.variance > 0 ? '+' : ''}${formatINR(r.variance)}${r.variancePct !== null ? ` (${r.variancePct.toFixed(1)}%)` : ''}`}
                      </td>
                      <td className="px-3 py-2 text-right text-[#475569]">
                        {formatINR((r.actPerUnit ?? r.stdPerUnit) || 0)}
                      </td>
                      <td className={`px-3 py-2 text-right font-semibold ${r.marginPct === null ? 'text-[#94a3b8]' : marginClass(r.marginPct)}`}>
                        {r.marginPct === null ? '—' : `${r.marginPct.toFixed(1)}%`}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span className="inline-flex px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 text-[10px] font-bold">{r.grade}</span>
                      </td>
                      <td className="px-3.5 py-2 text-right text-[#94a3b8] whitespace-nowrap">{shortDate(r.calculatedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Panel>
        </div>
      </div>
    </AppLayout>
  );
}
